import React, { useState } from "react";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import _ from "lodash";
import AttachmentMergesTable from "./AttachmentMergesTable.tsx";
import ContentBox from "../ContentBox/ContentBox";
import AttachmentMergeMediaObjects from "./AttachmentMergeMediaObjects.tsx";
import AttachmentMergeCombinable from "./AttachmentMergeCombinable.tsx";
import { Box, Button, Grid } from "@mui/material";
import attachmentMergesStyles from "./attachmentMergesStyles.ts";
import { Link, useNavigate } from "react-router-dom";
import { PlusOneOutlined } from "@mui/icons-material";
import AttachmentMergeDroppable from "./AttachmentMergeDroppable.tsx";
import deleteIcon from "../../assets/icon/delete_outline_black_24dp.svg";
import { useSnackbar } from "notistack";
import AttachmentMerge from "../../types/AttachmentMerge.ts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys, updateResource } from "../../services/ReactQuery/reactQueryService.ts";
import { MediaObject } from "../../types/MediaObject.ts";
import { HydraCollectionResult } from "../../types/HydraCollectionResult.ts";
import { apiPost } from "../../services/Api/apiCall.ts";

type AttachmentMergeViewProps = {
  attachmentMerge: AttachmentMerge;
};

export default function AttachmentMergeView({ attachmentMerge }: AttachmentMergeViewProps) {
  const [combinables, setCombinables] = useState<AttachmentMerge["mergeArray"]>(attachmentMerge.mergeArray);
  const [submitError, setSubmitError] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const updateMutation = useMutation({
    mutationFn: updateResource,
  });
  const mediaObjectIds = attachmentMerge.mergeArray.flat();
  const idFilterQuery = mediaObjectIds.map((mediaObjectId) => `id[]=${mediaObjectId}`).join("&");

  const { data: mediaObjectCollection } = useQuery<HydraCollectionResult<MediaObject>>({
    queryKey: queryKeys.collection("media_objects", { id: mediaObjectIds }),
    queryFn: () => fetchCollection("media_objects?" + idFilterQuery),
  });

  const mediaObjects = mediaObjectCollection?.["hydra:member"];

  if (!mediaObjects) {
    return null;
  }

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceItems = [...combinables[parseInt(result.source.droppableId)]];
    const [currentItem] = sourceItems.splice(result.source.index, 1);
    const updatedCombinables = [...combinables];

    if (result.destination.droppableId !== "filesToIgnore") {
      let targetItems =
        result.source.droppableId === result.destination.droppableId
          ? sourceItems
          : [...combinables[parseInt(result.destination.droppableId)]];
      targetItems.splice(result.destination.index, 0, currentItem);
      updatedCombinables[parseInt(result.destination.droppableId)] = targetItems;
    }

    updatedCombinables[parseInt(result.source.droppableId)] = sourceItems;
    setCombinables(updatedCombinables);
  };

  const addCombinable = () => {
    setCombinables([...combinables, []]);
  };

  const removeCombinable = (index: number) => {
    let combinablesCopy = _.cloneDeep(combinables);
    combinablesCopy.splice(index, 1);
    setCombinables(combinablesCopy);
  };

  const badAttachmentInCombinables = combinables.some((combinable) =>
    combinable.some((mediaObjectId) => mediaObjectId === 0),
  );

  let badAttachmentsError = "";
  if (badAttachmentInCombinables) {
    badAttachmentsError =
      "Achtung: Diese Nachricht enthält einen kaputten Anhang, dieser ist hier mit einem Warnzeichen versehen. Die Nachricht kann gespeichert werden, sobald die defekte Datei hier im Fenster in den Papierkorb verschoben wurde.\n" +
      "Bitte die beA-Nachricht vorher manuell im Webclient öffnen und die Datei manuell verarbeiten oder den Absender ggf. auf die defekte Datei hinweisen. Die Datei wird dem Fall nicht automatisch angehängt.\n" +
      "Bei Rückfragen bitte an das IT-Team wenden.";
  }

  const splitCombinables = () => {
    let split: Array<Array<number>> = [];

    _.forEach(combinables, (document) => {
      _.forEach(document, (mediaObjectId) => split.push([mediaObjectId]));
    });

    setCombinables(split);
  };

  const handleSubmit = async () => {
    setSubmitError("");

    if (_.some(combinables, (combinable) => combinable.length === 0)) {
      setSubmitError("Es darf keine leeren Dokumentenboxen geben. Zum Fortfahren die leeren Boxen bitte löschen");
      return;
    }

    try {
      //persist new merge split
      await updateMutation.mutateAsync({
        uri: "attachment_merges",
        id: attachmentMerge.id,
        data: {
          mergeArray: _.map(combinables, (document) => {
            return _.map(document, (mediaObjectId) => mediaObjectId);
          }),
        },
      });

      //start merge process
      await apiPost(attachmentMerge["@id"] + "/process");
      enqueueSnackbar("Dokument wurde erfolgreich verarbeitet", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
      navigate("/services/tools/anlagen");
    } catch (error) {
      alert("Es ist ein Fehler aufgetreten");
    }
  };

  return (
    <ContentBox>
      <AttachmentMergesTable attachmentMerges={[attachmentMerge]} />
      <AttachmentMergeMediaObjects mediaObjects={mediaObjects} />
      <Box>
        <Button fullWidth={false} onClick={() => splitCombinables()}>
          Dokumente aufteilen
        </Button>
      </Box>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "flex-start",
          }}
        >
          <Grid size={2}>
            <Box sx={{ height: "40px", display: "flex", alignItems: "center" }}>
              <strong>Papierkorb</strong>
            </Box>
            <AttachmentMergeDroppable
              combinable={[]}
              index={"filesToIgnore"}
              backgroundImage={deleteIcon}
              mediaObjects={mediaObjects}
            />
          </Grid>
          {_.map(combinables, (combinable, index) => (
            <Grid key={"box-" + index} size={3}>
              <AttachmentMergeCombinable
                combinable={combinable}
                index={index}
                removeCombinable={removeCombinable}
                mediaObjects={mediaObjects}
              />
            </Grid>
          ))}
          <Grid onClick={() => addCombinable()} sx={{ cursor: "pointer" }} size={2}>
            <Box sx={{ height: "40px", display: "flex", alignItems: "center" }}>
              <strong>Weiteres Dokument ergänzen</strong>
            </Box>
            <Box sx={attachmentMergesStyles.placeholderBox}>
              <PlusOneOutlined />
            </Box>
          </Grid>
        </Grid>
      </DragDropContext>
      <Box
        sx={{
          textAlign: "center",
          color: "red",
        }}
      >
        {submitError || badAttachmentsError}
      </Box>
      <Button
        sx={attachmentMergesStyles.button}
        variant={"contained"}
        fullWidth={false}
        onClick={() => handleSubmit()}
        disabled={badAttachmentInCombinables}
      >
        Dokumente erstellen
      </Button>
      <Button sx={attachmentMergesStyles.button} fullWidth={false} component={Link} to={"/services/tools/anlagen"}>
        Zurück
      </Button>
    </ContentBox>
  );
}
